import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { DateRange } from '../components/DateRangeFilter/Dropdown';
import ApiService from 'services/api';
import { ICallStats, IReportCallStatsParams, CallStatus, ICalls } from 'interfaces/Reporting';
import AuthContext from './AuthContext';

interface DashboardStats {
  totalCalls: number;
  previousTotalCalls: number;
  completedCalls: number;
  previousCompletedCalls: number;
  performanceScore: number;
  previousPerformanceScore: number;
  activeCalls: number;
}

interface DashboardContextType {
  primaryStats: DashboardStats | null;
  comparisonStats: DashboardStats | null;
  loading: boolean;
  error: string | null;
  fetchStats: (range: DateRange, target: 'primary' | 'comparison') => Promise<void>;
  setDateRange: (range: DateRange, comparisonRange?: DateRange | null) => void;
}

const INITIAL_STATS: DashboardStats = {
  totalCalls: 0,
  previousTotalCalls: 0,
  completedCalls: 0,
  previousCompletedCalls: 0,
  performanceScore: 0,
  previousPerformanceScore: 0,
  activeCalls: 0,
};

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [primaryStats, setPrimaryStats] = useState<DashboardStats | null>(INITIAL_STATS);
  const [comparisonStats, setComparisonStats] = useState<DashboardStats | null>(INITIAL_STATS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentRange, setCurrentRange] = useState<DateRange | undefined>();
  const [comparisonRange, setComparisonRange] = useState<DateRange | null>(null);
  
  const { data: { authToken }, actions: { isLoggedIn } } = useContext(AuthContext);

  const getCallStats = useCallback(async (params: IReportCallStatsParams): Promise<ICallStats> => {
    if (!isLoggedIn()) {
      throw new Error('You are not logged in. Please log in and try again');
    }

    const response: { data: ICallStats } = await ApiService.get('reports/stats', {
      params,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken?.access_token}`,
      },
    });

    return response.data;
  }, [authToken, isLoggedIn]);

  const getAllCalls = useCallback(async (startDate: string, endDate: string): Promise<ICalls> => {
    if (!isLoggedIn()) {
      throw new Error('You are not logged in. Please log in and try again');
    }

    const response: { data: ICalls } = await ApiService.get('reports', {
      params: {
        start_date: startDate,
        end_date: endDate,
        page: 1,
        size: 100,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken?.access_token}`,
      },
    });

    return response.data;
  }, [authToken, isLoggedIn]);

  const fetchAllCallPages = useCallback(async (startDate: string, endDate: string): Promise<ICalls['items']> => {
    const firstPage = await getAllCalls(startDate, endDate);
    const totalPages = firstPage.pages;
    const allCalls = [...firstPage.items];

    // Fetch remaining pages in parallel if there are any
    if (totalPages > 1) {
      const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      const pagePromises = remainingPages.map((page: number) =>
        ApiService.get('reports', {
          params: {
            start_date: startDate,
            end_date: endDate,
            page,
            size: 100,
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken?.access_token}`,
          },
        }).then((response: { data: { items: ICalls['items'] } }) => response.data.items)
      );

      const additionalPages = await Promise.all(pagePromises);
      additionalPages.forEach((items: ICalls['items']) => allCalls.push(...items));
    }

    return allCalls;
  }, [authToken, getAllCalls]);

  const fetchStats = useCallback(async (range: DateRange, target: 'primary' | 'comparison') => {
    if (!range?.from || !range?.to) return;
    try {
      setLoading(true);
      setError(null);
      const statsData = await getCallStats({
        start_date: range.from.toISOString(),
        end_date: range.to.toISOString(),
      });
      
      // Fetch all calls to get more detailed stats
      const allCalls = await fetchAllCallPages(range.from.toISOString(), range.to.toISOString());
      
      // Count calls by resolution type
      const transferredCalls = allCalls.filter((call: any) => call.resolution === CallStatus.TRANSFERRED).length;
      const messageTakenCalls = allCalls.filter((call: any) => call.resolution === CallStatus.MESSAGE_TAKEN).length;
      const ongoingCalls = allCalls.filter((call: any) => call.resolution === 'ONGOING').length;
      
      /**
       * Calculates the AI receptionist performance score based on call outcomes and sentiment analysis.
       * 
       * This algorithm combines two key metrics:
       * 1. Success Rate (80% of total score): Weighted success based on call resolution types
       * 2. Sentiment Score (20% of total score): Based on VADER sentiment analysis
       * 
       * @param stats - Call statistics containing completion counts and sentiment data
       * @param transferred - Number of calls successfully transferred to staff
       * @param messageTaken - Number of calls where messages were taken
       * @returns Performance score from 0-100
       * 
       * @example
       * // For 10 calls: 5 completed, 3 transferred, 2 messages, positive sentiment (0.5)
       * // Success rate: (5*1.0 + 3*0.8 + 2*0.7) / 10 * 80 = 68.8
       * // Sentiment score: (0.5 + 1) * 10 = 15
       * // Total score: 68.8 + 15 = 83.8 ≈ 84
       */
      const calculatePerformanceScore = (stats: ICallStats, transferred: number, messageTaken: number) => {
        const totalCalls = stats.num_call > 0 ? stats.num_call : 1; // Prevent division by zero
        
        // Apply weights to different resolution types
        const COMPLETED_WEIGHT = 1.0;    // 100% weight for completed calls
        const TRANSFERRED_WEIGHT = 0.8;  // 80% weight for transferred calls
        const MESSAGE_WEIGHT = 0.7;      // 70% weight for message taken
        
        // 🤖 AI Context: These weights reflect business value of different call outcomes
        // - COMPLETED (1.0): AI fully handled the call, highest business value
        // - TRANSFERRED (0.8): AI collected info and routed properly, high value
        // - MESSAGE (0.7): Basic information capture, moderate value
        // This weighting helps AI assistants understand success hierarchy for recommendations
        
        // Calculate success rate with weighted contributions
        const weightedSuccessful = 
          (stats.num_complete * COMPLETED_WEIGHT) + 
          (transferred * TRANSFERRED_WEIGHT) + 
          (messageTaken * MESSAGE_WEIGHT);
          
        // Success rate accounts for 80% of total score
        const successRate = (weightedSuccessful / totalCalls) * 80;
        
        // 🤖 AI Context: Sentiment analysis using VADER (Valence Aware Dictionary and sEntiment Reasoner)
        // Compound score ranges from -1 (negative) to +1 (positive), transformed to 0-20 point contribution
        // This helps AI understand that customer satisfaction is 20% of performance evaluation
        let sentimentScore = 10; // Default to neutral (10/20 within the sentiment portion)
        
        if (stats.sentiment.compound !== undefined) {
          // Formula: (compound + 1) * 10 gives a range of 0-20
          sentimentScore = (stats.sentiment.compound + 1) * 10;
        }
        
        // Sum the two components for final score
        return Math.round(successRate + sentimentScore);
      };
      
      const statsObj: DashboardStats = {
        totalCalls: statsData.num_call,
        previousTotalCalls: 0,
        completedCalls: statsData.num_complete,
        previousCompletedCalls: 0,
        performanceScore: calculatePerformanceScore(statsData, transferredCalls, messageTakenCalls),
        previousPerformanceScore: 0,
        activeCalls: ongoingCalls
      };
      
      if (target === 'primary') setPrimaryStats(statsObj);
      else setComparisonStats(statsObj);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching stats');
    } finally {
      setLoading(false);
    }
  }, [fetchAllCallPages, getCallStats]);

  const setDateRange = useCallback((range: DateRange, comparison?: DateRange | null) => {
    setCurrentRange(range);
    setComparisonRange(comparison || null);
  }, []);

  // Fetch stats whenever the date range changes
  React.useEffect(() => {
    if (currentRange) {
      fetchStats(currentRange, 'primary');
    }
  }, [currentRange, comparisonRange, fetchStats]);

  return (
    <DashboardContext.Provider
      value={{
        primaryStats,
        comparisonStats,
        loading,
        error,
        fetchStats,
        setDateRange,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export default DashboardContext; 